import { useState, useEffect } from 'react';
import { Table, Form, Button, InputGroup, Row, Col, Pagination, DropdownButton, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getBusinessPartners } from '../utils/api';
import Header from './Header';
import Loader from './Loader';
import { faPlus, faPencil, faFilter, faHandshake, faBoxArchive } from '@fortawesome/free-solid-svg-icons';
import ShowMenuButton from './ShowMenuButton';
import CreateEditBusinessPartnerModal from './Modals/CreateEditBusinessPartnerModal';
import { useDataStore } from '../Store';
import Error from './Error';
import ViewBusinessPartnerModal from './Modals/ViewBusinessPartnerModal';
import ArchiveBusinessPartnerModal from './Modals/ArchiveBusinessPartnerModal';

const BusinessPartners = ({ itemSelected, lockedBusinessPartnerTypeFilter }) => {

    const selectedDealer = useDataStore((state) => state.selectedDealer);
    const selectedEntity = useDataStore((state) => state.selectedEntity);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [businessPartnerTypeFilter, setBusinessPartnerTypeFilter] = useState(lockedBusinessPartnerTypeFilter || [0]);

    const [selectedBusinessPartner, setSelectedBusinessPartner] = useState(null);
    const [showCreateBusinessPartnerModal, setShowCreateBusinessPartnerModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showArchiveConfirmationModal, setShowArchiveConfirmationModal] = useState(false);


    const fetchData = async () => {
        setLoading(true);
        setError(null);
        const data = await getBusinessPartners(selectedEntity, page, 30, searchText, businessPartnerTypeFilter);
        if (data) {
            setData(data);
        }
        else {
            setError('Failed to fetch data');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [selectedDealer, searchText, businessPartnerTypeFilter, page]);


    const importAssets = () => {
        setSelectedBusinessPartner(null);
        setShowCreateBusinessPartnerModal(true);
        return;
    };
    const rowClicked = (o) => {
        if (itemSelected) {
            itemSelected(o);
            return;
        }
        setSelectedBusinessPartner(o);
        setShowViewModal(true);
    }
    const handleActionClick = (name, o) => {
        if (name === 'View') {
            rowClicked(o);
            return;
        }

        if (name === 'Edit') {
            setSelectedBusinessPartner(o);
            setShowCreateBusinessPartnerModal(true);
            return;
        }

        if (name === 'Archive') {
            setSelectedBusinessPartner(o);
            setShowArchiveConfirmationModal(true);
            return;
        }
    }

    const buttons = [
        { id: 0, label: 'Add Business Partner', variant: 'primary', icon: faPlus, onClick: () => importAssets() },
    ]

    const lastPage = data.LastPage;

    return (
        <div style={{ padding: 10 }}>
            {!itemSelected && <Header title='Business Partners' description="Manage business partners incluing customers, dealers, lien holders and vendors" buttons={buttons} />}
            <Row className="mb-3">
                <Col md={12}>
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Search Business Partners..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    fetchData();
                                }
                            }}
                        />
                        <Button variant="outline-secondary" onClick={fetchData}>
                            Search
                        </Button>
                    </InputGroup>
                </Col>
            </Row>
            {loading ? <Loader /> : error ? <Error /> : (
                <>
                    <Row className="mb-3 mt-3">
                        <Col>
                            <div style={{ overflowX: 'auto', width: '100%' }}>
                                <Table striped bordered hover style={{ minHeight: 190, overflow: 'visible' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Id
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                    Type
                                                    <DropdownButton
                                                        id="type-filter"
                                                        title={<FontAwesomeIcon sstyle={{ fontSize: '4px' }} icon={faFilter} />}
                                                        variant={itemSelected ? 'secondary' : 'primary'}
                                                        style={{ height: 24 }}
                                                        className="td-filter-dropdown-button"
                                                        disabled={itemSelected}
                                                        onSelect={(eventKey) => {
                                                            setBusinessPartnerTypeFilter([eventKey])
                                                        }}
                                                    >
                                                        {data?.Filters?.find(c => c.Field == "types")?.FilterEntries?.map((d, idx) => (
                                                            <Dropdown.Item key={d.Key} eventKey={d.Key} active={businessPartnerTypeFilter?.includes(d.Key)}>{d.Text}</Dropdown.Item>
                                                        ))}


                                                    </DropdownButton>
                                                </div>
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Name
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Address
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Address2
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                City
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                State
                                            </th>
                                            <th style={{ whiteSpace: 'nowrap' }}>
                                                Zip
                                            </th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data?.Results?.map((d, idx) => (
                                            <tr key={idx}>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Id}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.BusinessPartnerType?.Description}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Name}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Address?.AddressLine1}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Address?.AddressLine2}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Address?.City}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Address?.State}</td>
                                                <td style={{ whiteSpace: 'nowrap', verticalAlign: 'middle', cursor: 'pointer' }} onClick={() => rowClicked(d)}>{d?.Address?.PostalCode}</td>
                                                {!itemSelected && <td style={{ whiteSpace: 'nowrap', textAlign: 'center', verticalAlign: 'middle' }}>{<ShowMenuButton menuItems={[
                                                    { icon: <FontAwesomeIcon icon={faHandshake} />, name: 'View', target: d, onClick: handleActionClick },
                                                    { icon: <FontAwesomeIcon icon={faPencil} />, name: 'Edit', target: d, onClick: handleActionClick },
                                                    { icon: <FontAwesomeIcon icon={faBoxArchive} />, name: 'Archive', target: d, onClick: handleActionClick },
                                                ]} />}</td>}
                                                {itemSelected && <td style={{ whiteSpace: 'nowrap', textAlign: 'center', verticalAlign: 'middle' }}><Button onClick={() => itemSelected(d)}>Select</Button></td>}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    </Row>

                    <Row className="mb-3 mt-3">
                        <Col className="d-flex justify-content-center">
                            <Pagination>
                                <Pagination.First disabled={page <= 1} onClick={() => setPage(1)} />
                                <Pagination.Prev disabled={page <= 1} onClick={() => setPage(page - 1)} />
                                <Pagination.Item key={page} active={true}>
                                    {page}
                                </Pagination.Item>
                                <Pagination.Next disabled={page >= lastPage} onClick={() => setPage(page + 1)} />
                                <Pagination.Last disabled={page >= lastPage} onClick={() => setPage(lastPage)} />
                            </Pagination>
                        </Col>
                    </Row>


                    <ViewBusinessPartnerModal
                        show={showViewModal}
                        onHide={() => setShowViewModal(false)}
                        businessPartner={selectedBusinessPartner}
                    />



                    <CreateEditBusinessPartnerModal
                        show={showCreateBusinessPartnerModal}
                        onHide={(shouldRefresh) => {
                            setShowCreateBusinessPartnerModal(false);
                            if (shouldRefresh)
                                fetchData();
                        }}
                        businessPartner={selectedBusinessPartner}
                    />

                    <ArchiveBusinessPartnerModal
                        show={showArchiveConfirmationModal}
                        onHide={(shouldRefresh) => {
                            setShowArchiveConfirmationModal(false);
                            if (shouldRefresh)
                                fetchData();
                        }}
                        businessPartner={selectedBusinessPartner}
                    />

                </>
            )
            }
        </div >
    );
};

export default BusinessPartners;
